import React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { getIconUrl } from "@/lib/directus";
import Button, { ButtonType } from "@/components/reusable/Button";
import { LazyMotion, domAnimation, m } from "framer-motion";

export interface HeadingImageTextData {
  sectionTitle: string;
  image: string;
  image_url?: string;
  alt?: string;
  sections: any[];
  textAlignment?: "left" | "right";
}

interface HeadingImageTextProps {
  data: HeadingImageTextData;
}

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function HeadingImageText({ data }: HeadingImageTextProps) {
  const {
    sectionTitle,
    image,
    image_url,
    alt,
    sections,
    textAlignment = "left",
  } = data;
  const isTextLeft = textAlignment === "left";

  const imageSrc = image_url ? image_url : getIconUrl(image);
  const imageAlt = alt || sectionTitle || "Section image";

  return (
    <LazyMotion features={domAnimation}>
      <section className="w-full flex flex-col desktop:flex-row bg-[#5E6A79]">
        {isTextLeft && (
        <div
          className={cn(
            "w-full desktop:w-1/2 flex flex-col gap-12 justify-center order-1 desktop:order-1",
            "desktop:ml-[7.5vw] desktop:mr-[3.33vw]",
            "py-12",
            "max-w-[85vw] mx-auto",
            "desktop:max-w-[unset]"
          )}
        >
          {sections.map((section, idx) => (
            <m.div
              key={idx}
              className="flex flex-col gap-6"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="font-sans text-h3-mobile md:text-h3-tablet desktop:text-h3-desktop uppercase text-[#FFFFFF]">
                {section.item.title}
              </div>
              <div className="font-sans text-paragraph-large text-[#FFFFFF]">
                {section.item.text}
              </div>
              {section.item.buttonTitle && section.item.buttonLink && (
                <Button
                  link={section.item.buttonLink}
                  type={ButtonType.quaternary}
                  className="md:w-fit border-white text-white self-end py-3 px-6 text-[18px] leading-[24px] tracking-[0.01em]"
                >
                  {section.item.buttonTitle}
                  <span className="ml-2 text-lg">&#8250;</span>
                </Button>
              )}
            </m.div>
          ))}
        </div>
      )}

      <div
        className={cn(
          "w-full desktop:w-1/2 flex flex-col",
          isTextLeft ? "order-2 desktop:order-2" : "order-1 desktop:order-1"
        )}
      >
        <div className="w-full bg-[#27344A] flex items-center justify-center py-12 px-4">
          <h2 className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-[#F3E6D8] text-center">
            {sectionTitle}
          </h2>
        </div>
        <div className="relative w-full h-[400px] desktop:h-full">
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            className="object-cover object-center"
          />
        </div>
      </div>

      {!isTextLeft && (
        <div
          className={cn(
            "w-full desktop:w-1/2 flex flex-col gap-12 justify-center order-2 desktop:order-2",
            "desktop:mr-[7.5vw] desktop:ml-[3.33vw]",
            "py-12",
            "max-w-[85vw] mx-auto",
            "desktop:max-w-[unset]"
          )}
        >
          {sections.map((section, idx) => (
            <m.div
              key={idx}
              className="flex flex-col gap-6"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="font-sans text-h3-mobile md:text-h3-tablet desktop:text-h3-desktop uppercase text-[#FFFFFF]">
                {section.item.title}
              </div>
              <div className="font-sans text-paragraph-large text-[#FFFFFF]">
                {section.item.text}
              </div>
              {section.item.buttonTitle && section.item.buttonLink && (
                <Button
                  link={section.item.buttonLink}
                  type={ButtonType.quaternary}
                  className="md:w-fit border-white text-white self-end py-3 px-6 text-[18px] leading-[24px] tracking-[0.01em]"
                >
                  {section.item.buttonTitle}
                  <span className="ml-2 text-lg">&#8250;</span>
                </Button>
              )}
            </m.div>
          ))}
        </div>
        )}
      </section>
    </LazyMotion>
  );
}
